import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Activity, MapPin, BookOpen, TrendingUp, Calendar, Mail } from "lucide-react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { useNavigate } from 'react-router-dom';

interface CustomDashboardProps {
  userRole: string;
  userRegion?: string;
}

interface DashboardStats {
  totalMembers?: number;
  totalRegions?: number;
  activeActivities?: number;
  upcomingConferences?: number;
  totalNewsletters?: number;
  totalMagazines?: number;
  pendingSubmissions?: number;
  regionalMembers?: number;
  regionalActivities?: number;
  regionalPrayerRequests?: number;
  newMessages?: number;
  totalPrayerRequests?: number;
  totalTestimonies?: number;
  totalArticles?: number;
  pendingTestimonies?: number;
  pendingArticles?: number;
}

export const CustomDashboard: React.FC<CustomDashboardProps> = ({ userRole, userRegion }) => {
  const [stats, setStats] = useState<DashboardStats>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
  }, [userRole, userRegion]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      if (userRole === 'global_admin' || userRole === 'president') {
        await fetchGlobalStats();
      } else if (userRole === 'kit_admin') {
        await fetchKitStats();
      } else if (userRole === 'regional_admin') {
        await fetchRegionalStats();
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setLoading(false);
    }
  };

  const fetchGlobalStats = async () => {
    const [membersResult, regionsResult, activitiesResult, conferencesResult, messagesResult, prayerResult] = await Promise.all([
      supabase.from('members').select('id', { count: 'exact', head: true }),
      supabase.from('regions').select('id', { count: 'exact', head: true }),
      supabase.from('activities').select('id', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('conferences').select('id', { count: 'exact', head: true }).eq('status', 'upcoming'),
      supabase.from('contact_messages').select('id', { count: 'exact', head: true }).eq('status', 'new'),
      supabase.from('prayer_requests').select('id', { count: 'exact', head: true }).eq('status', 'active')
    ]);

    setStats({
      totalMembers: membersResult.count || 0,
      totalRegions: regionsResult.count || 0,
      activeActivities: activitiesResult.count || 0,
      upcomingConferences: conferencesResult.count || 0,
      newMessages: messagesResult.count || 0,
      totalPrayerRequests: prayerResult.count || 0,
    });
  };

  const fetchKitStats = async () => {
    const [
      newslettersResult, 
      magazinesResult, 
      submissionsResult, 
      testimoniesResult,
      approvedTestimoniesResult,
      approvedArticlesResult,
      articlesResult
    ] = await Promise.all([
      supabase.from('newsletters').select('id', { count: 'exact', head: true }),
      supabase.from('magazines').select('id', { count: 'exact', head: true }),
      supabase.from('newsletter_submissions').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('testimonies').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('testimonies').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
      supabase.from('articles').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
      supabase.from('articles').select('id', { count: 'exact', head: true }).eq('status', 'pending')
    ]);

    setStats({
      totalNewsletters: newslettersResult.count || 0,
      totalMagazines: magazinesResult.count || 0,
      pendingSubmissions: (submissionsResult.count || 0) + (testimoniesResult.count || 0) + (articlesResult.count || 0),
      totalTestimonies: approvedTestimoniesResult.count || 0,
      totalArticles: approvedArticlesResult.count || 0,
      pendingTestimonies: testimoniesResult.count || 0,
      pendingArticles: articlesResult.count || 0
    });
  };

  const fetchRegionalStats = async () => {
    if (!userRegion) return;

    const [membersResult, activitiesResult, prayerResult, conferencesResult, messagesResult] = await Promise.all([
      supabase.from('members').select('id', { count: 'exact', head: true }).eq('region_id', userRegion).eq('is_active', true),
      supabase.from('activities').select('id', { count: 'exact', head: true }).eq('region_id', userRegion).eq('is_active', true),
      supabase.from('prayer_requests').select('id', { count: 'exact', head: true }).eq('region_id', userRegion).eq('status', 'active'),
      supabase.from('conferences').select('id', { count: 'exact', head: true }).eq('region_id', userRegion).eq('status', 'upcoming'),
      supabase.from('contact_messages').select('id', { count: 'exact', head: true }).eq('region_id', userRegion).eq('status', 'new')
    ]);

    setStats({
      regionalMembers: membersResult.count || 0,
      regionalActivities: activitiesResult.count || 0,
      regionalPrayerRequests: prayerResult.count || 0,
      upcomingConferences: conferencesResult.count || 0,
      newMessages: messagesResult.count || 0,
    });
  };

  const renderGlobalAdminDashboard = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Members</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalMembers || 0}</div>
          <p className="text-xs text-muted-foreground">Across all regions</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Regions</CardTitle>
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalRegions || 0}</div>
          <p className="text-xs text-muted-foreground">Active regions</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Activities</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.activeActivities || 0}</div>
          <p className="text-xs text-muted-foreground">Currently running</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Upcoming Conferences</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.upcomingConferences || 0}</div>
          <p className="text-xs text-muted-foreground">Scheduled events</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Prayer Requests</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalPrayerRequests || 0}</div>
          <p className="text-xs text-muted-foreground">Active requests</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">New Messages</CardTitle>
          <Mail className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${(stats.newMessages || 0) > 0 ? 'text-primary' : ''}`}>
            {stats.newMessages || 0}
          </div>
          <p className="text-xs text-muted-foreground">Unread inquiries</p>
        </CardContent>
      </Card>
    </div>
  );

  const renderKitAdminDashboard = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Testimonies</CardTitle>
          <BookOpen className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalTestimonies || 0}</div>
          <p className="text-xs text-muted-foreground">Approved testimonies</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Pending Testimonies</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${(stats.pendingTestimonies || 0) > 0 ? 'text-primary' : ''}`}>
            {stats.pendingTestimonies || 0}
          </div>
          <p className="text-xs text-muted-foreground">Awaiting review</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Articles</CardTitle>
          <BookOpen className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalArticles || 0}</div>
          <p className="text-xs text-muted-foreground">Approved articles</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Pending Articles</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${(stats.pendingArticles || 0) > 0 ? 'text-primary' : ''}`}>
            {stats.pendingArticles || 0}
          </div>
          <p className="text-xs text-muted-foreground">Awaiting review</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Newsletters</CardTitle>
          <BookOpen className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalNewsletters || 0}</div>
          <p className="text-xs text-muted-foreground">Published newsletters</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Magazines</CardTitle>
          <BookOpen className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalMagazines || 0}</div>
          <p className="text-xs text-muted-foreground">Published magazines</p>
        </CardContent>
      </Card>
    </div>
  );

  const renderRegionalAdminDashboard = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Regional Members</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.regionalMembers || 0}</div>
          <p className="text-xs text-muted-foreground">Active members</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Activities</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.regionalActivities || 0}</div>
          <p className="text-xs text-muted-foreground">In your region</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Prayer Requests</CardTitle>
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.regionalPrayerRequests || 0}</div>
          <p className="text-xs text-muted-foreground">Active requests</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Upcoming Conferences</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.upcomingConferences || 0}</div>
          <p className="text-xs text-muted-foreground">Scheduled events</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">New Messages</CardTitle>
          <Mail className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${(stats.newMessages || 0) > 0 ? 'text-primary' : ''}`}>
            {stats.newMessages || 0}
          </div>
          <p className="text-xs text-muted-foreground">From your region</p>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="space-y-0 pb-2">
              <div className="h-4 bg-muted rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded animate-pulse mb-2" />
              <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(userRole === 'global_admin' || userRole === 'president') && renderGlobalAdminDashboard()}
      {userRole === 'kit_admin' && renderKitAdminDashboard()}
      {userRole === 'regional_admin' && renderRegionalAdminDashboard()}
    </div>
  );
};
